export const OPENAI_ADS_ATTRIBUTION_COOKIE = "__oppref";
export const OPENAI_ADS_OPPREF_MAX_LENGTH = 500;

// RFC 6265 cookie-octet characters. This permits a URL-encoded opaque value
// while rejecting separators, whitespace, control characters and oversized
// input. The value itself is never decoded or otherwise transformed.
const OPENAI_ADS_OPPREF_PATTERN = /^[\x21\x23-\x2B\x2D-\x3A\x3C-\x5B\x5D-\x7E]+$/;

/**
 * Treat the click reference as an opaque value. Invalid input is discarded
 * rather than cleaned, decoded or normalized.
 */
export function sanitizeOpenAIAdsOppref(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null;

  if (
    value.length > OPENAI_ADS_OPPREF_MAX_LENGTH
    || !OPENAI_ADS_OPPREF_PATTERN.test(value)
  ) {
    return null;
  }

  return value;
}

/**
 * Reads the raw value of a query parameter without URL-decoding it. Returning
 * null for duplicate values avoids making an arbitrary attribution choice.
 */
export function getRawOpenAIAdsOppref(fullPath: unknown): string | null {
  if (typeof fullPath !== "string") return null;

  const queryStart = fullPath.indexOf("?");
  if (queryStart === -1) return null;

  const fragmentStart = fullPath.indexOf("#", queryStart);
  const query = fullPath.slice(
    queryStart + 1,
    fragmentStart === -1 ? undefined : fragmentStart,
  );
  const matches = query
    .split("&")
    .filter(part => part.split("=", 1)[0] === "oppref");

  if (matches.length !== 1) return null;

  const match = matches[0];
  if (!match) return null;

  const separator = match.indexOf("=");
  return sanitizeOpenAIAdsOppref(separator === -1 ? "" : match.slice(separator + 1));
}

/** Reads a raw cookie without applying the decoder used by framework helpers. */
export function getRawOpenAIAdsOpprefCookie(cookieHeader: unknown): string | null {
  if (typeof cookieHeader !== "string") return null;

  for (const part of cookieHeader.split(";")) {
    const separator = part.indexOf("=");
    if (
      separator === -1
      || part.slice(0, separator).trim() !== OPENAI_ADS_ATTRIBUTION_COOKIE
    ) {
      continue;
    }

    return sanitizeOpenAIAdsOppref(part.slice(separator + 1));
  }

  return null;
}

export function resolveOpenAIAdsOppref(
  cookieValue: unknown,
  storedValue: unknown,
): string | null {
  return sanitizeOpenAIAdsOppref(cookieValue)
    ?? sanitizeOpenAIAdsOppref(storedValue);
}
